import { configure } from 'mobx';
import { History } from 'history';
import { getBasename } from '../../libs/urlUtils';
import { ApiClient } from '../../apiClient/ApiClient';
import { ErrorReporter } from '../../errorHandling/ErrorReporter';
import { i18n } from '../../i18n/i18n';
import { syncHistoryWithStore } from '../../stores/reactRouterStore/syncHistoryWithStore';
import {
  OverriddenCategoryNamesStore,
  OverriddenCategoryNamesStoreState,
} from '../../stores/OverriddenCategoryNamesStore';
import { RoutingStore, RoutingStoreState } from './RoutingStore';
import { RedirectStore } from './RedirectStore';
import { TemplatesStore, TemplatesStoreState } from './TemplatesStore';
import { CategoriesStore, CategoriesStoreState } from './CategoriesStore';
import { BaseURLStore } from './BaseURLStore';
import { BILoggerStore } from './BILoggerStore';
import { ConfigStore, ConfigStoreState } from './ConfigStore';
import { HeaderStore } from './HeaderStore';
import { FedopsLoggerStore } from './FedopsLoggerStore';
import { SearchWithSuggestionsStore } from './SearchWithSuggestionsStore';

configure({ enforceActions: 'observed', safeDescriptors: false });

export interface StoresState {
  config: ConfigStoreState;
  categories: CategoriesStoreState;
  templates: TemplatesStoreState;
  routing: RoutingStoreState;
  overriddenCategoryNames: OverriddenCategoryNamesStoreState;
}

export type InitialStoresState = Partial<StoresState> &
  Required<Pick<StoresState, 'config' | 'routing' | 'overriddenCategoryNames'>>;

export interface Stores {
  headerStore: HeaderStore;
  baseURLStore: BaseURLStore;
  routingStore: RoutingStore;
  redirectStore: RedirectStore;
  templatesStore: TemplatesStore;
  categoriesStore: CategoriesStore;
  biLoggerStore: BILoggerStore;
  fedopsLoggerStore: FedopsLoggerStore;
  configStore: ConfigStore;
  searchWithSuggestionsStore: SearchWithSuggestionsStore;
  overriddenCategoryNamesStore: OverriddenCategoryNamesStore;
}

export function getState(stores: Stores): StoresState {
  return {
    config: stores.configStore.serialize(),
    categories: stores.categoriesStore.serialize(),
    templates: stores.templatesStore.serialize(),
    routing: stores.routingStore.serialize(),
    overriddenCategoryNames: stores.overriddenCategoryNamesStore.serialize(),
  };
}

export function createStores(parameters: {
  i18nInstance: i18n;
  apiClient: ApiClient;
  history: History;
  errorReporter: ErrorReporter;
  initialState: InitialStoresState;
}): Stores {
  const { i18nInstance, history, apiClient, initialState, errorReporter } = parameters;

  const routingStore = new RoutingStore({}, initialState.routing);
  // The store matches against basename-relative route patterns, but on the client the
  // raw history (createBrowserHistory, basename handled by the Router) carries the full
  // path including the basename. Strip it so locations reaching the store are always
  // basename-relative. stripBasename only removes a matching prefix, so it is a safe
  // no-op for paths that are already relative (e.g. the server's memory history).
  syncHistoryWithStore(history, routingStore, getBasename(initialState.config.baseURL));
  const configStore = new ConfigStore(initialState.config);
  const overriddenCategoryNamesStore = new OverriddenCategoryNamesStore(
    i18nInstance,
    initialState.overriddenCategoryNames,
  );
  const headerStore = new HeaderStore({ routingStore });
  const baseURLStore = new BaseURLStore(configStore);
  const categoriesStore = new CategoriesStore({ apiClient, errorReporter }, initialState.categories);
  const fedopsLoggerStore = new FedopsLoggerStore();
  const biLoggerStore = new BILoggerStore();
  const searchWithSuggestionsStore = new SearchWithSuggestionsStore({
    apiClient,
    errorReporter,
    routingStore,
    biLoggerStore,
    categoriesStore,
    configStore,
  });
  const templatesStore = new TemplatesStore(
    {
      apiClient,
      routingStore,
      errorReporter,
      categoriesStore,
      baseURLStore,
      biLoggerStore,
      fedopsLoggerStore,
      headerStore,
      searchWithSuggestionsStore,
      configStore,
    },
    initialState.templates,
  );
  const redirectStore = new RedirectStore({
    categoriesStore,
    routingStore,
    configStore,
    templatesStore,
  });

  biLoggerStore.init({
    routingStore,
    templatesStore,
    headerStore,
    baseURLStore,
    templatePageSessionId: initialState.config.templatePageSessionId,
    configStore,
  });

  return {
    headerStore,
    configStore,
    baseURLStore,
    routingStore,
    redirectStore,
    templatesStore,
    categoriesStore,
    biLoggerStore,
    fedopsLoggerStore,
    searchWithSuggestionsStore,
    overriddenCategoryNamesStore,
  };
}

export async function storesInitialFetch(stores: Stores): Promise<void> {
  await stores.categoriesStore.initialFetch();
  await stores.templatesStore.fetch();
}
